import type { Goal } from '@medplum/fhirtypes';

import { Grid, Stack, Tag, type TagProps, Text } from '@eluve/components';

import type { FhirView } from '../../core/view-contract';
import { resourceDisplayName } from '../accessors';
import { formatFhirDate } from '../observation/format';

import { formatGoalTarget } from './format';

/**
 * The exported Goal component. Goal cards render it in lists, so the row is
 * the primary variant.
 */
export const GoalView: FhirView<Goal> = (props) => {
  if (props.ctx.inList) return <GoalRowView {...props} />;
  return <GoalReadView {...props} />;
};

/**
 * Product projection of HL7 goal-achievement onto the three tones a goal
 * list communicates: met, on track, at risk. Goals without an
 * achievementStatus (e.g. fresh AI drafts) get no chip.
 */
export type GoalProgress = {
  label: string;
  color: TagProps['color'];
};

const AT_RISK_CODES = new Set([
  'worsening',
  'no-progress',
  'not-attainable',
  'not-achieved',
]);

export function goalProgress(goal: Goal): GoalProgress | undefined {
  const code = goal.achievementStatus?.coding?.[0]?.code;
  if (!code) return undefined;
  if (code === 'achieved' || code === 'sustaining') {
    return { label: 'Met', color: 'positive' };
  }
  if (AT_RISK_CODES.has(code)) {
    return { label: 'At risk', color: 'warning' };
  }
  return { label: 'On track', color: 'gray' };
}

/** Achievement chip (Met / On track / At risk); nothing without a status. */
export function GoalAchievementTag({ goal }: { goal: Goal }) {
  const progress = goalProgress(goal);
  if (!progress) return null;
  return (
    <Tag pill subtle size="xs" color={progress.color} label={progress.label} />
  );
}

function goalDateText(data: Goal): string | undefined {
  const date = formatFhirDate(data.startDate ?? data.statusDate);
  if (!date) return undefined;
  return data.startDate ? `Since ${date}` : `Updated ${date}`;
}

/** Goal-list row: description, achievement chip, start/status date. */
const GoalRowView: FhirView<Goal> = ({ data }) => {
  const date = goalDateText(data);
  return (
    <Grid
      data-view="goal-row"
      align="center"
      className="grid-cols-[minmax(0,1fr)_auto] gap-x-4 py-2"
    >
      <Stack direction="horizontal" gap={2} align="center" className="min-w-0">
        <Text inline size="sm" weight="regular" className="truncate">
          {resourceDisplayName(data)}
        </Text>
        <GoalAchievementTag goal={data} />
      </Stack>
      {date && (
        <Text inline size="xs" color="tertiary">
          {date}
        </Text>
      )}
    </Grid>
  );
};

/** Standalone card: the goal is the page's subject. */
const GoalReadView: FhirView<Goal> = ({ data, ctx }) => {
  const date = goalDateText(data);
  const targetText = formatGoalTarget(data);
  return (
    <div
      data-view="goal-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold">
          {resourceDisplayName(data)}
        </Text>
        <GoalAchievementTag goal={data} />
      </Stack>
      <Stack
        direction="horizontal"
        wrap
        align="stretch"
        className="mt-1 gap-x-4"
      >
        {date && (
          <Text inline size="xs" color="tertiary">
            {date}
          </Text>
        )}
        {targetText && (
          <Text inline size="xs" color="tertiary">
            Target {targetText}
          </Text>
        )}
        {data.lifecycleStatus !== 'active' &&
          data.lifecycleStatus !== 'accepted' && (
            <Text inline size="xs" color="tertiary">
              {data.lifecycleStatus.replace(/-/g, ' ')}
            </Text>
          )}
      </Stack>
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
